import { FilePlus2, FileText, MoreHorizontal, Pencil, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { PageHeader } from '@/components/common/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { fmtDate } from '@/features/shared/format'
import { HrSurface } from '@/features/hr/personel/components/HrSurface'
import { Select } from '@/components/ui/select'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useCategoryLabel, useDocumentTypes } from './api'
import { useSablonlar } from './hooks/useTemplateStore'
import { HR_DOCUMENT_WRITE_PERM } from '../permissions'

export function BelgelerListPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const canWrite = can(HR_DOCUMENT_WRITE_PERM)
  const navigate = useNavigate()
  const sablonlar = useSablonlar()
  const types = useDocumentTypes().data ?? []
  const categoryLabel = useCategoryLabel()
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('all')
  const [kat, setKat] = useState('all')

  const rows = useMemo(() => {
    const n = q.trim().toLocaleLowerCase('tr')
    return sablonlar.filter((s) => {
      if (status !== 'all' && s.status !== status) return false
      if (kat !== 'all' && s.kategori !== kat) return false
      if (!n) return true
      return (
        s.ad.toLocaleLowerCase('tr').includes(n) ||
        s.aciklama.toLocaleLowerCase('tr').includes(n)
      )
    })
  }, [sablonlar, q, status, kat])

  function openEditor(id: string) {
    navigate(`/hr/belgeler/sablonlar/${id}/duzenle`)
  }

  return (
    <HrSurface>
      <PageHeader
        title={t('hr.belgeler.listTitle')}
        description={t('hr.belgeler.listSubtitle')}
        actions={
          canWrite ? (
            <Button size="sm" className="shadow-sm" onClick={() => navigate('/hr/belgeler/sablonlar/yeni')}>
              <FilePlus2 />
              {t('hr.belgeler.newTemplate')}
            </Button>
          ) : undefined
        }
      />

      <div className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-xs">
        <div className="flex flex-wrap items-center gap-3 border-b p-4">
          <div className="relative min-w-0 flex-[1_1_16rem]">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="h-10 rounded-xl bg-muted/40 pl-9 shadow-none"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('hr.belgeler.search')}
            />
          </div>
          <Select
            size="lg"
            className="w-auto min-w-[9.5rem] rounded-xl"
            value={kat}
            onChange={setKat}
            options={[
              { value: 'all', label: t('hr.belgeler.allCategories') },
              ...types.map((k) => ({ value: k.code, label: k.name })),
            ]}
          />
          <Select
            size="lg"
            className="w-auto min-w-[9.5rem] rounded-xl"
            value={status}
            onChange={setStatus}
            options={[
              { value: 'all', label: t('hr.belgeler.allStatuses') },
              { value: 'taslak', label: t('hr.belgeler.status.taslak') },
              { value: 'yayinda', label: t('hr.belgeler.status.yayinda') },
              { value: 'arsiv', label: t('hr.belgeler.status.arsiv') },
            ]}
          />
        </div>

        <Table className="hr-table">
          <THead>
            <TR className="hover:bg-transparent">
              <TH>{t('hr.belgeler.columns.name')}</TH>
              <TH>{t('hr.belgeler.columns.category')}</TH>
              <TH>{t('hr.belgeler.columns.method')}</TH>
              <TH>{t('hr.belgeler.columns.version')}</TH>
              <TH>{t('hr.belgeler.columns.status')}</TH>
              <TH>{t('hr.belgeler.columns.updated')}</TH>
              <TH className="w-12">{t('hr.belgeler.columns.actions')}</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((s) => (
              <TR
                key={s.id}
                className={canWrite ? 'cursor-pointer' : undefined}
                onClick={canWrite ? () => openEditor(s.id) : undefined}
              >
                <TD>
                  <div className="flex items-center gap-2.5">
                    <span className="flex size-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <FileText className="size-4" />
                    </span>
                    <div className="min-w-0">
                      <div className="truncate font-semibold">{s.ad}</div>
                      <div className="truncate text-xs text-muted-foreground">{s.aciklama}</div>
                    </div>
                  </div>
                </TD>
                <TD>{categoryLabel(s.kategori)}</TD>
                <TD>{t(`hr.belgeler.method.${s.method}`)}</TD>
                <TD className="tabular-nums">v{s.currentVersion}</TD>
                <TD>
                  <Badge
                    tone={s.status === 'yayinda' ? 'success' : s.status === 'taslak' ? 'info' : 'neutral'}
                  >
                    {t(`hr.belgeler.status.${s.status}`)}
                  </Badge>
                </TD>
                <TD className="whitespace-nowrap text-muted-foreground">{fmtDate(s.updatedAt)}</TD>
                <TD onClick={(e) => e.stopPropagation()}>
                  {canWrite ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="size-8" aria-label={t('hr.belgeler.columns.actions')}>
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditor(s.id)}>
                          <Pencil className="size-3.5" />
                          {t('hr.belgeler.actions.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/hr/belgeler/sablonlar/${s.id}/surum`)}>
                          {t('hr.belgeler.actions.versions')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </TD>
              </TR>
            ))}
            {!rows.length ? (
              <TR>
                <TD colSpan={7} className="py-12 text-center text-sm text-muted-foreground">
                  {t('common.empty')}
                </TD>
              </TR>
            ) : null}
          </TBody>
        </Table>
      </div>
    </HrSurface>
  )
}
